/* eslint-disable no-unused-vars */
import {createSlice} from '@reduxjs/toolkit';
import get from 'lodash.get';
import {
    DEFAULT_TEMPLATES_OPTIONS,
    PACKAGES_PER_PAGE_OPTIONS,
    PACKAGE_DIR_OPTIONS,
    PACKAGE_SORT_OPTIONS,
    VISIBILITY_ACCOUNT
} from '@constants/index';
import {getRecipientsEmailsFromPackage} from '@utils/helpers';
import {fetchAvatarsByEmails} from '@components/Packages/Recipients/recipientsSlice';
import {getErrorMessage} from '@utils/requests/error';
import {flushTemplateCache} from '@components/AI/aiSlice';
import {bulkApi} from '@api/bulk/api';
import {transactionApi} from '@api/transaction/api';
import {documentApi} from '@api/document/api';
import {recipientApi} from '@api/recipient/api';
import {getApprovalsForTransactionByDocumentId} from '@components/Packages/utils/getApprovalsAndFields';
import {setApprovalsForTransaction} from '@components/Transactions/transactionsSlice';
import {selectActiveAccountId} from '@components/Login/userSlice';

export const templatesSlice = createSlice({
    name: 'templates',
    initialState: {
        templates: [],
        count: 0,
        templatesPerPage: PACKAGES_PER_PAGE_OPTIONS[10],
        isTemplatesFetching: false,
        fetchingTemplatesError: null,
        isDeletingTemplate: false,
        deleteTemplateError: null,
        options: DEFAULT_TEMPLATES_OPTIONS,
        fastTrackUrls: {},
        isFastTrackUrlsFetching: false,
        fetchingFastTrackUrlsError: null,
        isFetchingFastTrackInfo: false,
        fetchingFastTrackInfoError: null,
        isUpdatingFastTrackInfo: false,
        updatingFastTrackInfoError: null

    },
    reducers: {
        setTemplates: (state, action) => {
            state.templates = action.payload;
        },
        setCount: (state, action) => {
            state.count = action.payload;
        },
        setTemplatesPerPage: (state, action) => {
            state.templatesPerPage = action.payload;
        },
        setIsTemplatesFetching: (state, action) => {
            state.isTemplatesFetching = action.payload;
        },
        setFetchingTemplatesError: (state, action) => {
            state.fetchingTemplatesError = action.payload;
        },
        setOptions: (state, action) => {
            state.options = action.payload;
        },
        setIsDeletingTemplate: (state, action) => {
            state.isDeletingTemplate = action.payload;
        },
        setDeleteTemplateError: (state, action) => {
            state.deleteTemplateError = action.payload;
        },
        setFastTrackUrls: (state, action) => {
            state.fastTrackUrls = action.payload;
        },
        setIsFastTrackUrlsFetching: (state, action) => {
            state.isFastTrackUrlsFetching = action.payload;
        },
        setFetchingFastTrackUrlsError: (state, action) => {
            state.fetchingFastTrackUrlsError = action.payload;
        },
        setFetchingFastTrackInfoError: (state, action) => {
            state.fetchingFastTrackInfoError = action.payload;
        },
        setUpdatingFastTrackInfoError: (state, action) => {
            state.updatingFastTrackInfoError = action.payload;
        }

    }
});

export const {
    setTemplates,
    setCount,
    setTemplatesPerPage,
    setIsTemplatesFetching,
    setFetchingTemplatesError,
    setOptions,
    setIsDeletingTemplate,
    setDeleteTemplateError,
    setFastTrackUrls,
    setIsFastTrackUrlsFetching,
    setFetchingFastTrackUrlsError,
    setFetchingFastTrackInfoError,
    setUpdatingFastTrackInfoError
} = templatesSlice.actions;

const TEMPLATES_SORT_FIELD_MAP = {
    [PACKAGE_SORT_OPTIONS.CREATION]: 'created_at',
    [PACKAGE_SORT_OPTIONS.UPDATED]: 'updated_at',
    [PACKAGE_SORT_OPTIONS.NAME]: 'name'
};

function getMappedTemplatesSort(sort, dir) {
    if (!sort) return undefined;
    const mapped = TEMPLATES_SORT_FIELD_MAP[sort] || sort;
    const isDesc = (dir || '').toLowerCase() === PACKAGE_DIR_OPTIONS.DESC;
    return isDesc ? `-${mapped}` : mapped;
}

function getIsPlaceholderRecipient(recipient) {
    return !recipient.email && !recipient.group_id;
}

function getBulkSendableFlag(recipients, documentApprovals) {
    const placeholderIds = recipients
        .filter(getIsPlaceholderRecipient)
        .map((r) => r.id);
    if (!placeholderIds.length) return false;

    const allApprovals = Object.values(documentApprovals).flat();
    return placeholderIds.some((rid) => allApprovals.some((a) => a.recipient_id === rid));
}

async function getEnrichedTemplates(baseTemplates, apiOptions = {}) {
    return Promise.all(baseTemplates.map(async (template) => {
        const [documentsSettled, recipientsSettled] = await Promise.allSettled([
            documentApi.getAllDocuments(template.id, undefined, apiOptions),
            recipientApi.getAllRecipients(template.id, undefined, apiOptions)
        ]);
        const documents = documentsSettled.status === 'fulfilled' ? (documentsSettled.value || []) : [];
        const recipients = recipientsSettled.status === 'fulfilled' ? (recipientsSettled.value || []) : [];

        const documentApprovals = await getApprovalsForTransactionByDocumentId(
            template.id,
            documents.map((doc) => doc.id),
            apiOptions
        );

        return {
            ...template,
            documents,
            recipients,
            bulkSendable: getBulkSendableFlag(recipients, documentApprovals),
            documentApprovals
        };
    }));
}

export const fetchTemplates = ({
    headers, baseUrl, options: fetchOptions = {}, isSilent = false
} = {}) => async (dispatch, getState) => {
    const state = getState();
    const accountId = selectActiveAccountId(state) || get(state, 'user.account.id');
    const {templates} = state;
    const {
        from,
        to,
        sort,
        dir,
        search,
        visibility
    } = templates.options;

    const perPage = Math.max((to || PACKAGES_PER_PAGE_OPTIONS[10]) - (from || 1) + 1, 1);
    const page = Math.max(Math.ceil((from || 1) / perPage), 1);

    const params = {page, per_page: perPage};
    if (search) params.search = search;
    if (visibility === VISIBILITY_ACCOUNT) params.shared = true;
    const mappedSort = getMappedTemplatesSort(sort, dir);
    if (mappedSort) params.sort = mappedSort;

    const apiOptions = {};
    if (headers) apiOptions.headers = headers;
    if (fetchOptions.timeout) apiOptions.timeout = fetchOptions.timeout;
    if (baseUrl) apiOptions.baseUrl = baseUrl;

    if (!isSilent) dispatch(setIsTemplatesFetching(true));
    dispatch(setFetchingTemplatesError(null));

    try {
        const result = await transactionApi.getTemplates({accountId, params, ...apiOptions});
        const baseTemplates = Array.isArray(result?.data) ? result.data : [];
        const totalCount = get(result, 'meta.total')
            ?? get(result, 'data.meta.total')
            ?? baseTemplates.length;

        if (!isSilent) {
            dispatch(setTemplates(baseTemplates));
            dispatch(setCount(totalCount));
            dispatch(setIsTemplatesFetching(false));
        }
        dispatch(setFetchingTemplatesError(null));

        const enrichedTemplates = await getEnrichedTemplates(baseTemplates, apiOptions);
        const recipientEmails = getRecipientsEmailsFromPackage(enrichedTemplates);
        await dispatch(fetchAvatarsByEmails({
            baseUrl, headers, emails: recipientEmails, options: fetchOptions
        }));

        enrichedTemplates.forEach((template) => {
            if (template.documentApprovals) {
                dispatch(setApprovalsForTransaction({
                    transactionId: template.id,
                    documentApprovals: template.documentApprovals
                }));
            }
        });
        const templatesForStore = enrichedTemplates.map(({documentApprovals, ...rest}) => rest);

        dispatch(setTemplates(templatesForStore));
        if (isSilent) dispatch(setCount(totalCount));
        return true;
    } catch (err) {
        if (!isSilent) dispatch(setIsTemplatesFetching(false));
        dispatch(setFetchingTemplatesError(getErrorMessage(err)));
        return false;
    }
};


export const deleteTemplate = (packageId) => async (dispatch, getState) => {
    const state = getState();
    const accountId = selectActiveAccountId(state) || get(state, 'user.account.id');
    const userId = get(state, 'user.user.id');

    dispatch(setIsDeletingTemplate(true));
    dispatch(setDeleteTemplateError(null));

    try {
        await transactionApi.deleteTemplate({
            accountId,
            userId,
            templateId: packageId
        });
        dispatch(flushTemplateCache({templateId: packageId}));
        return true;
    } catch (err) {
        dispatch(setDeleteTemplateError(getErrorMessage(err)));
        return false;
    } finally {
        dispatch(setIsDeletingTemplate(false));
    }
};

export const fetchFastTrackUrls = (accountId, templateId) => async (dispatch, getState) => {
    dispatch(setIsFastTrackUrlsFetching(true));
    dispatch(setFetchingFastTrackUrlsError(null));

    try {
        const result = await bulkApi.getFastTrackUrls({accountId, templateId});
        const {templates} = getState();

        const sendingUrl = result.data.sending_url || '';
        const signingUrl = result.data.signing_url || '';

        dispatch(setFastTrackUrls({
            ...templates.fastTrackUrls,
            [templateId]: {sendingUrl, signingUrl}
        }));
        dispatch(setIsFastTrackUrlsFetching(false));
        return result.data;
    } catch (err) {
        dispatch(setIsFastTrackUrlsFetching(false));
        dispatch(setFetchingFastTrackUrlsError(getErrorMessage(err)));
        return false;
    }
};

export const fetchFastTrackSendingInfo = (accountId, templateId, options) => async (dispatch) => {
    dispatch(setFetchingFastTrackInfoError(null));

    try {
        const result = await bulkApi.getFastTrackSending({accountId, templateId, options});
        return result;
    } catch (err) {
        dispatch(setFetchingFastTrackInfoError(getErrorMessage(err)));
        return false;
    }
};

export const fetchFastTrackSigningInfo = (accountId, templateId, options) => async (dispatch) => {
    dispatch(setFetchingFastTrackInfoError(null));

    try {
        const result = await bulkApi.getFastTrackSigning({accountId, templateId, options});
        return result;
    } catch (err) {
        dispatch(setFetchingFastTrackInfoError(getErrorMessage(err)));
        return false;
    }
};

export const storeFastTrack = (accountId, templateId, data, options) => async (dispatch) => {
    dispatch(setUpdatingFastTrackInfoError(null));

    try {
        const result = await bulkApi.storeFastTrack({accountId, templateId, data, options});
        return result;
    } catch (err) {
        dispatch(setUpdatingFastTrackInfoError(getErrorMessage(err)));
        return false;
    }
};

export const selectTemplates = (state) => state.templates.templates;
export const selectCount = (state) => state.templates.count;
export const selectTemplatesPerPage = (state) => state.templates.templatesPerPage;
export const selectIsTemplatesFetching = (state) => state.templates.isTemplatesFetching;
export const selectFetchingTemplatesError = (state) => state.templates.fetchingTemplatesError;
export const selectIsVisibilityFetching = (state) => state.templates.isVisibilityFetching;
export const selectIsDeletingTemplate = (state) => state.templates.isDeletingTemplate;
export const selectOptions = (state) => state.templates.options;
export const selectFastTrackUrls = (state) => state.templates.fastTrackUrls;
export const selectIsFastTrackUrlsFetching = (state) => state.templates.isFastTrackUrlsFetching;
export const selectFetchingFastTrackUrlsError = (state) => state.templates.fetchingFastTrackUrlsError;
export const selectFetchingFastTrackInfoError = (state) => state.templates.fetchingFastTrackInfoError;
export const selectUpdatingFastTrackInfoError = (state) => state.templates.updatingFastTrackInfoError;

export default templatesSlice.reducer;
